// Feedback Type, Status, Priority Definitions and Configurations

// Type definitions
const FEEDBACK_TYPES = {
  BUG: 'bug',
  FEATURE: 'feature'
};

// Error Log Status definitions
export const ERROR_LOG_STATUSES = {
  NEW: 'new',
  VIEWED: 'viewed',
  PROMOTED: 'promoted',
  IGNORED: 'ignored'
};

// Error Log Source definitions
const ERROR_LOG_SOURCES = {
  VUE: 'vue',
  WINDOW: 'window',
  UNHANDLED_REJECTION: 'unhandled-rejection',
  CONSOLE: 'console.error',
  MANUAL: 'manual'
};

// Kanban status definitions
export const FEEDBACK_STATUSES = {
  BACKLOG: 'backlog',
  IN_PROGRESS: 'in_progress',
  STUCK: 'stuck',
  COMPLETE: 'complete'
};

// Priority definitions
const FEEDBACK_PRIORITIES = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
};

// Version status definitions
export const VERSION_STATUSES = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  DEPLOYED: 'deployed'
}

// Version status configurations
export const VERSION_STATUS_CONFIG = {
  [VERSION_STATUSES.PLANNED]: {
    label: 'Planned',
    value: 'planned',
    icon: 'calendar',
    color: 'hsl(204, 71%, 53%)',
    className: 'is-info'
  },
  [VERSION_STATUSES.IN_PROGRESS]: {
    label: 'In Progress',
    value: 'in_progress',
    icon: 'hammer',
    color: 'hsl(34, 100%, 56%)',
    className: 'is-warning'
  },
  [VERSION_STATUSES.DEPLOYED]: {
    label: 'Deployed',
    value: 'deployed',
    icon: 'rocket',
    color: 'hsl(158, 80%, 35%)',
    className: 'is-success'
  }
}

// Map old status values to new kanban statuses
const STATUS_MIGRATION_MAP = {
  'new': FEEDBACK_STATUSES.BACKLOG,
  'viewed': FEEDBACK_STATUSES.BACKLOG,
  'in-progress': FEEDBACK_STATUSES.IN_PROGRESS,
  'resolved': FEEDBACK_STATUSES.COMPLETE
};

// Migrate old status to new kanban status
export const migrateStatus = (oldStatus) => {
  return STATUS_MIGRATION_MAP[oldStatus] || oldStatus || FEEDBACK_STATUSES.BACKLOG;
};

// Type configurations with icons and colors
const FEEDBACK_TYPE_CONFIG = {
  [FEEDBACK_TYPES.BUG]: {
    label: 'Bug',
    value: 'bug',
    icon: 'bug',
    color: 'hsl(348, 86%, 61%)',
    className: 'is-danger'
  },
  [FEEDBACK_TYPES.FEATURE]: {
    label: 'Feature Request',
    value: 'feature',
    icon: 'lightbulb',
    color: 'hsl(217, 71%, 53%)',
    className: 'is-info'
  }
};

// Priority configurations with icons and colors
const FEEDBACK_PRIORITY_CONFIG = {
  [FEEDBACK_PRIORITIES.CRITICAL]: {
    label: 'Critical',
    value: 'critical',
    icon: 'circle-exclamation',
    color: 'hsl(348, 86%, 45%)',
    bgColor: 'hsla(348, 86%, 45%, 0.15)',
    className: 'priority-critical'
  },
  [FEEDBACK_PRIORITIES.HIGH]: {
    label: 'High',
    value: 'high',
    icon: 'arrow-up',
    color: 'hsl(14, 100%, 53%)',
    bgColor: 'hsla(14, 100%, 53%, 0.15)',
    className: 'priority-high'
  },
  [FEEDBACK_PRIORITIES.MEDIUM]: {
    label: 'Medium',
    value: 'medium',
    icon: 'minus',
    color: 'hsl(204, 71%, 53%)',
    bgColor: 'hsla(204, 71%, 53%, 0.15)',
    className: 'priority-medium'
  },
  [FEEDBACK_PRIORITIES.LOW]: {
    label: 'Low',
    value: 'low',
    icon: 'arrow-down',
    color: 'hsl(0, 0%, 48%)',
    bgColor: 'hsla(0, 0%, 48%, 0.15)',
    className: 'priority-low'
  }
};

// Column configurations for Kanban boards
const BUG_COLUMNS = [
  {
    value: FEEDBACK_STATUSES.BACKLOG,
    label: 'Backlog',
    icon: 'inbox',
    color: 'hsl(0, 0%, 48%)'
  },
  {
    value: FEEDBACK_STATUSES.IN_PROGRESS,
    label: 'In Progress',
    icon: 'circle-play',
    color: 'hsl(34, 100%, 56%)'
  },
  {
    value: FEEDBACK_STATUSES.STUCK,
    label: 'Stuck',
    icon: 'circle-exclamation',
    color: 'hsl(348, 86%, 61%)'
  },
  {
    value: FEEDBACK_STATUSES.COMPLETE,
    label: 'Complete',
    icon: 'circle-check',
    color: 'hsl(158, 80%, 35%)'
  }
];

const FEATURE_COLUMNS = [
  {
    value: FEEDBACK_STATUSES.BACKLOG,
    label: 'Backlog',
    icon: 'lightbulb',
    color: 'hsl(0, 0%, 48%)'
  },
  {
    value: FEEDBACK_STATUSES.IN_PROGRESS,
    label: 'In Progress',
    icon: 'hammer',
    color: 'hsl(217, 71%, 53%)'
  },
  {
    value: FEEDBACK_STATUSES.COMPLETE,
    label: 'Complete',
    icon: 'rocket',
    color: 'hsl(158, 80%, 35%)'
  }
];

// Error Log Kanban columns
export const ERROR_LOG_COLUMNS = [
  {
    value: ERROR_LOG_STATUSES.NEW,
    label: 'New Errors',
    icon: 'circle-exclamation',
    color: 'hsl(348, 86%, 61%)'
  },
  {
    value: ERROR_LOG_STATUSES.VIEWED,
    label: 'Viewed',
    icon: 'eye',
    color: 'hsl(204, 71%, 53%)'
  },
  {
    value: ERROR_LOG_STATUSES.PROMOTED,
    label: 'Promoted to Bug',
    icon: 'bug',
    color: 'hsl(141, 53%, 53%)'
  },
  {
    value: ERROR_LOG_STATUSES.IGNORED,
    label: 'Ignored',
    icon: 'eye-slash',
    color: 'hsl(0, 0%, 48%)'
  }
];

// Error Log Source configurations
const ERROR_LOG_SOURCE_CONFIG = {
  [ERROR_LOG_SOURCES.VUE]: {
    label: 'Vue Error',
    icon: 'vuejs',
    iconPrefix: 'fab',
    color: 'hsl(153, 47%, 49%)',
    className: 'is-success'
  },
  [ERROR_LOG_SOURCES.WINDOW]: {
    label: 'Window Error',
    icon: 'window-maximize',
    iconPrefix: 'fas',
    color: 'hsl(348, 86%, 61%)',
    className: 'is-danger'
  },
  [ERROR_LOG_SOURCES.UNHANDLED_REJECTION]: {
    label: 'Promise Rejection',
    icon: 'bolt',
    iconPrefix: 'fas',
    color: 'hsl(48, 100%, 50%)',
    className: 'is-warning'
  },
  [ERROR_LOG_SOURCES.CONSOLE]: {
    label: 'Console Error',
    icon: 'terminal',
    iconPrefix: 'fas',
    color: 'hsl(0, 0%, 29%)',
    className: 'is-dark'
  },
  [ERROR_LOG_SOURCES.MANUAL]: {
    label: 'Manual Capture',
    icon: 'hand-pointer',
    iconPrefix: 'fas',
    color: 'hsl(204, 71%, 53%)',
    className: 'is-info'
  }
};

// Get priority options for dropdowns
export const getFeedbackPriorityOptions = (type = 'bug') => {
  const options = Object.values(FEEDBACK_PRIORITY_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    icon: config.icon,
    color: config.color
  }));

  // Feature requests don't have "critical" priority
  if (type === 'feature') {
    return options.filter(opt => opt.value !== 'critical');
  }

  return options;
};

// Get icon for feedback type
export const getFeedbackTypeIcon = (type) => {
  const config = FEEDBACK_TYPE_CONFIG[type];
  return config ? config.icon : 'question-circle';
};

// Get color for feedback type
export const getFeedbackTypeColor = (type) => {
  const config = FEEDBACK_TYPE_CONFIG[type];
  return config ? config.color : 'hsl(0, 0%, 48%)';
};

// Get label for feedback type
export const getFeedbackTypeLabel = (type) => {
  const config = FEEDBACK_TYPE_CONFIG[type];
  return config ? config.label : 'Unknown';
};

// Get icon for feedback priority
export const getFeedbackPriorityIcon = (priority) => {
  const config = FEEDBACK_PRIORITY_CONFIG[priority];
  return config ? config.icon : 'question-circle';
};

// Get style object for feedback priority tag
export const getFeedbackPriorityStyle = (priority) => {
  const config = FEEDBACK_PRIORITY_CONFIG[priority];
  if (!config) {
    return {
      backgroundColor: 'hsla(0, 0%, 48%, 0.15)',
      color: 'hsl(0, 0%, 48%)',
      border: '1px solid hsl(0, 0%, 48%)'
    };
  }
  return {
    backgroundColor: config.bgColor,
    color: config.color,
    border: `1px solid ${config.color}`
  };
};

// Get label for feedback priority
export const getFeedbackPriorityLabel = (priority) => {
  const config = FEEDBACK_PRIORITY_CONFIG[priority];
  return config ? config.label : 'Unknown';
};

// Format relative time (e.g., "2 days ago")
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  return `${diffMonths}mo ago`;
};

// Truncate message for card display
export const truncateMessage = (message, maxLength = 80) => {
  if (!message) return '';
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength).trim() + '...';
};

// Group feedback items by status for kanban
export const groupFeedbackByStatus = (items, type = 'bug') => {
  const columns = type === 'feature' ? FEATURE_COLUMNS : BUG_COLUMNS;
  const grouped = {};

  columns.forEach(col => {
    grouped[col.value] = [];
  });

  items.forEach(item => {
    const status = migrateStatus(item.status);
    if (grouped[status]) {
      grouped[status].push({
        ...item,
        status // Use migrated status
      });
    } else {
      // Default to backlog if status doesn't match
      grouped[FEEDBACK_STATUSES.BACKLOG].push({
        ...item,
        status: FEEDBACK_STATUSES.BACKLOG
      });
    }
  });

  // Sort each column by order (for backlog) or by priority then timestamp
  Object.keys(grouped).forEach(status => {
    grouped[status].sort((a, b) => {
      // If in backlog, sort by order field first
      if (status === FEEDBACK_STATUSES.BACKLOG) {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
      }

      // Then sort by priority
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.priority] || 3;
      const bPriority = priorityOrder[b.priority] || 3;
      if (aPriority !== bPriority) return aPriority - bPriority;

      // Finally sort by timestamp (newest first)
      const aTime = a.timestamp?.toDate?.()?.getTime() || 0;
      const bTime = b.timestamp?.toDate?.()?.getTime() || 0;
      return bTime - aTime;
    });
  });

  return grouped;
};

// Group error logs by status for kanban
export const groupErrorLogsByStatus = (logs) => {
  const grouped = {};

  ERROR_LOG_COLUMNS.forEach(col => {
    grouped[col.value] = [];
  });

  logs.forEach(log => {
    const status = log.status || ERROR_LOG_STATUSES.NEW;
    if (grouped[status]) {
      grouped[status].push(log);
    } else {
      // Default to 'new' if status doesn't match
      grouped[ERROR_LOG_STATUSES.NEW].push(log);
    }
  });

  // Sort each group by lastSeen (most recent first)
  Object.keys(grouped).forEach(status => {
    grouped[status].sort((a, b) => {
      const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
      return bTime - aTime;
    });
  });

  return grouped;
};

// Get error log source config
export const getErrorLogSourceConfig = (source) => {
  return ERROR_LOG_SOURCE_CONFIG[source] || ERROR_LOG_SOURCE_CONFIG[ERROR_LOG_SOURCES.MANUAL];
};


// Numeric semver compare ('3.10.0' > '3.9.0'); missing parts count as 0
export const compareSemver = (a, b) => {
  const ap = (a || '0.0.0').split('.').map(Number)
  const bp = (b || '0.0.0').split('.').map(Number)
  return (ap[0] - bp[0]) || (ap[1] - bp[1]) || (ap[2] - bp[2])
}

const toMs = (t) => {
  if (!t) return 0
  if (typeof t.toDate === 'function') return t.toDate().getTime()
  const ms = new Date(t).getTime()
  return Number.isNaN(ms) ? 0 : ms
}

// Strips logger %c styling noise so an error reads as a one-line title
export const cleanErrorMessage = (message) => {
  if (!message) return ''
  let cleaned = message
    .replace(/color:\s*#[a-fA-F0-9]+;[^;]*;[^;]*;[^;]*;/g, '')
    .replace(/color:\s*#[a-fA-F0-9]+;[^[]*\[/g, '[')
    .replace(/background:\s*#[a-fA-F0-9]+;[^;]*;[^;]*;[^;]*;/g, '')
    .replace(/%c\[[^\]]*\]\s*%c\[[^\]]*\]\s*[^[]+/g, '')
    .replace(/%c[^%]*?(?=%c|$)/g, '')
    .replace(/%c/g, '')
    .replace(/^[^[]*?\[/, '[')
    .replace(/^[^[]*(\[[^\]]+\])/, '$1')
    .trim()

  if (cleaned.startsWith('color:') || cleaned.startsWith('background:')) {
    const match = cleaned.match(/(\[[^\]]+\].*|Global Error.*|Failed to.*|Cannot read.*|Error:.*)/i)
    if (match) cleaned = match[1]
  }
  return cleaned.trim() || message
}

/**
 * Link an error log to the bug it belongs to, by groupingKey.
 * - 'open': an unfinished bug already tracks it — don't promote a duplicate
 * - 'regressed': the bug shipped in a version deployed BEFORE this error was
 *   first seen, so the fix didn't hold. Time-based on purpose: dedup never
 *   refreshes context.appVersion, and package.json bumps aren't enforced.
 */
export const matchErrorToFeedback = (errorLog, feedbackList, versions) => {
  const key = errorLog.groupingKey
  if (!key) return null
  const linked = feedbackList.filter(f => f.groupingKey === key)
  const open = linked.find(f => migrateStatus(f.status) !== FEEDBACK_STATUSES.COMPLETE)
  if (open) return { kind: 'open', feedback: open }

  const seenAt = toMs(errorLog.firstSeen || errorLog.createdAt)
  for (const f of linked) {
    const version = versions.find(v => v.id === f.versionId)
    if (version?.status === VERSION_STATUSES.DEPLOYED && toMs(version.deployedAt) < seenAt) {
      return { kind: 'regressed', feedback: f, version }
    }
  }
  return null
}

// Feedback doc body for a promoted error log. Pure (no Firestore imports) so
// scripts/fetchErrorLogs.js builds the exact same bug as the admin UI.
export const buildFeedbackFromErrorLog = (errorLog, { logId, regressionOf } = {}) => {
  const ctx = errorLog.context || {}
  const crumbs = (errorLog.breadcrumbs || [])
    .map((c, i) => `${i + 1}. [${c.type}] ${c.message}${c.timestamp ? ` (${new Date(c.timestamp).toLocaleTimeString()})` : ''}`)
    .join('\n')

  const stepsToReproduce = [
    `**Error occurred on route:** ${ctx.route || 'unknown'}`,
    crumbs && `**User Actions (Breadcrumbs):**\n${crumbs}`,
    `**Stack Trace:**\n\`\`\`\n${errorLog.stack || 'No stack trace'}\n\`\`\``
  ].filter(Boolean).join('\n\n')

  const adminNotes = [
    `**Promoted from Error Log**`,
    `- Error Log ID: ${logId || 'N/A'}`,
    `- Source: ${errorLog.source}`,
    `- Occurrences: ${errorLog.occurrenceCount || 1}`,
    `- First seen: ${errorLog.firstSeen || 'N/A'}`,
    `- Last seen: ${errorLog.lastSeen || 'N/A'}`,
    `- App version: ${ctx.appVersion || 'N/A'}`,
    regressionOf && `- **Regression** of "${regressionOf.feedback.message}" (fixed in v${regressionOf.version.number})`,
    '',
    `**User Context:**`,
    `- User: ${ctx.userEmail || 'N/A'} (${ctx.userId || 'not logged in'})`,
    `- Company ID: ${ctx.companyId || 'N/A'}`,
    `- Impersonating: ${ctx.isImpersonating ? 'Yes' : 'No'}`,
    '',
    `**Browser Context:**`,
    `- Browser: ${ctx.browser || 'N/A'} · ${ctx.deviceType || 'N/A'} · ${ctx.screenSize || 'N/A'}`,
    `- User Agent: ${ctx.userAgent || 'N/A'}`
  ].filter(line => line !== null && line !== undefined && line !== false).join('\n')

  return {
    type: 'bug',
    status: FEEDBACK_STATUSES.BACKLOG,
    priority: regressionOf ? 'high' : 'medium',
    message: cleanErrorMessage(errorLog.message),
    pageUrl: ctx.pageUrl || '',
    browserInfo: ctx.browser || '',
    userId: ctx.userId || null,
    userEmail: ctx.userEmail || 'System (Auto-captured)',
    userName: 'Auto-captured Error',
    companyId: ctx.companyId || null,
    companyName: null,
    isImpersonating: ctx.isImpersonating || false,
    stepsToReproduce,
    expectedBehavior: '',
    screenshot: null,
    consoleLogs: errorLog.consoleLogs || [],
    adminNotes,
    // Links future occurrences back to this bug (open / regressed badges)
    groupingKey: errorLog.groupingKey || null,
    reportedIn: ctx.appVersion || null,
    versionId: null
  }
}
